// AddApp — manual application entry. Writes via POST /api/applications.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import { apiPost, ApiClientError } from '../lib/api';
import { useZh } from '../lib/useProduct';
import { stageLabel } from '../lib/stageLabels';
import type { ApplicationStage } from '../lib/types';

const STAGES: ApplicationStage[] = ['Evaluated', 'Applied', 'Responded', 'Phone screen', 'Interview', 'Offer', 'Rejected', 'Discarded'];

export default function AddApp() {
  const nav = useNavigate();
  const zh = useZh();
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [stage, setStage] = useState<ApplicationStage>('Evaluated');
  const [score, setScore] = useState('3.5');
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [warn, setWarn] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => apiPost('/api/applications', { company, role, stage, score: parseFloat(score), url: url || undefined, notes: notes || undefined }),
    onSuccess: () => {
      setWarn(zh ? '已保存，正在跳转到申请列表…' : 'Saved to batch/tracker-additions/. Run `node merge-tracker.mjs` to merge into applications.md.');
      setTimeout(() => nav('/apps'), 1500);
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.code === 'duplicate') {
        setWarn(zh ? '这个公司+职位已经在列表里了，请到申请列表页更新它。' : 'A row already exists for this company+role. Update it from the pipeline page.');
      } else {
        setWarn((err as Error).message);
      }
    },
  });

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/apps')}>
        {zh ? '← 申请列表' : '← Pipeline'}
      </button>
      <PageHeader
        eyebrow={zh ? '添加申请 · 手动' : 'Add application · manual'}
        title={zh ? '手动记一条。' : 'Log one by hand.'}
        sub={zh
          ? '当一个职位不是从扫描器来的——内推、直接邮件、或你自己找到的——用这个手动添加。'
          : 'Use this when a role came in outside the scanner — a referral, a direct email, or something you found yourself.'}
      />
      <div className="card" style={{ padding: 26, maxWidth: 640 }}>
        <Field label={zh ? '公司' : 'Company'}>
          <input value={company} onChange={(e) => setCompany(e.target.value)} style={inputStyle} placeholder={zh ? '高盛' : 'Anthropic'} />
        </Field>
        <Field label={zh ? '职位' : 'Role'}>
          <input value={role} onChange={(e) => setRole(e.target.value)} style={inputStyle} placeholder={zh ? '投行分析师（暑期）' : 'Research Engineer, Alignment'} />
        </Field>
        <Field label={zh ? '阶段' : 'Stage'}>
          <select value={stage} onChange={(e) => setStage(e.target.value as ApplicationStage)} style={inputStyle}>
            {STAGES.map((s) => <option key={s} value={s}>{stageLabel(s, zh)}</option>)}
          </select>
        </Field>
        <Field label={zh ? '评分 (0–5)' : 'Score (0–5)'}>
          <input type="number" min={0} max={5} step={0.1} value={score} onChange={(e) => setScore(e.target.value)} style={inputStyle} />
        </Field>
        <Field label={zh ? '链接（选填）' : 'URL (optional)'}>
          <input value={url} onChange={(e) => setUrl(e.target.value)} style={inputStyle} placeholder="https://…" />
        </Field>
        <Field label={zh ? '备注' : 'Notes'}>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder={zh ? '一句话说明为什么重要。' : 'One line about why it matters.'} />
        </Field>
        {warn && (
          <div style={{ padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)', marginBottom: 14 }}>
            {warn}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn ghost" onClick={() => nav('/apps')}>{zh ? '取消' : 'Cancel'}</button>
          <div style={{ flex: 1 }} />
          <button className="btn accent lg" disabled={!company || !role || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? (zh ? '保存中…' : 'Saving…') : (zh ? '保存申请' : 'Save application')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="tab" style={{ marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};
